import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { api } from '../lib/api.ts';
import { useAuthStore, type Me } from '../store/auth.ts';
import { subscriptionsApi } from '../features/subscriptions/api.ts';
import { PlanCard } from '../features/dashboard/PlanCard.tsx';

export function Profile(): JSX.Element {
  const setUser = useAuthStore.setState;
  const [me, setMe] = useState<Me | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Plan lives here rather than on Home: it is an account-level fact, not a
  // launcher concern. Home only interrupts when the plan actually gates
  // launching (GRACE/BLOCKED), via its alert banner.
  const { data: sub } = useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: subscriptionsApi.myStatus,
  });

  // Profile form
  const [name, setName] = useState('');
  const [nameDv, setNameDv] = useState('');
  const [designationDv, setDesignationDv] = useState('');
  const [designationEn, setDesignationEn] = useState('');
  const [section, setSection] = useState('');
  const [savePending, setSavePending] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Signature
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [sigBlobUrl, setSigBlobUrl] = useState<string | null>(null);

  // Password
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [pwPending, setPwPending] = useState(false);

  async function refreshMe(): Promise<void> {
    const r = await api.get<{ user: Me }>('/users/me');
    setMe(r.data.user);
    setName(r.data.user.name ?? '');
    setNameDv(r.data.user.nameDv ?? '');
    setDesignationDv(r.data.user.designationDv ?? '');
    setDesignationEn(r.data.user.designationEn ?? '');
    setSection(r.data.user.section ?? '');
  }

  useEffect(() => {
    void refreshMe();
  }, []);

  // Fetch the auth-gated signature as a blob so <img src> works.
  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    async function load(): Promise<void> {
      if (!me?.hasSignature) {
        setSigBlobUrl(null);
        return;
      }
      try {
        const r = await api.get<Blob>('/users/me/signature/file', { responseType: 'blob' });
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setSigBlobUrl(url);
      } catch {
        if (!cancelled) setSigBlobUrl(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [me?.hasSignature]);

  async function saveProfile(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSaveMsg(null);
    setSavePending(true);
    try {
      const r = await api.patch<{ user: Me }>('/users/me', {
        name,
        nameDv: nameDv || null,
        designationDv: designationDv || null,
        designationEn: designationEn || null,
        section: section || null,
      });
      setMe(r.data.user);
      setUser((s) => ({ ...s, user: r.data.user }));
      setSaveMsg('Saved.');
    } catch {
      setSaveMsg('Could not save. Please try again.');
    } finally {
      setSavePending(false);
    }
  }

  async function uploadSig(): Promise<void> {
    setError(null);
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', f);
      const r = await api.post<{ user: Me }>('/users/me/signature', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMe(r.data.user);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string; error?: string } } })?.response?.data;
      setError(detail?.detail ?? detail?.error ?? 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  async function removeSig(): Promise<void> {
    setError(null);
    try {
      await api.delete('/users/me/signature');
      await refreshMe();
    } catch {
      setError('Could not remove signature.');
    }
  }

  async function changePassword(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setPwMsg(null);
    setPwErr(null);
    setPwPending(true);
    try {
      // Changing the password revokes ALL of this user's existing sessions
      // server-side (refresh tokens + SSO) and returns a fresh token pair for
      // THIS tab. Adopt it, or the store keeps the now-revoked tokens and the
      // next silent refresh bounces the user to /login on the tab they just
      // changed the password from.
      const { data } = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: Me;
      }>('/users/me/password', { current, next });
      useAuthStore.setState({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      });
      setCurrent('');
      setNext('');
      // Server-side, the new password is propagated to every linked app.
      setPwMsg('Password updated everywhere.');
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setPwErr(status === 400 ? 'Current password is incorrect.' : 'Could not change password.');
    } finally {
      setPwPending(false);
    }
  }

  if (!me) {
    return (
      <div className="page">
        <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
          Loading profile…
        </p>
      </div>
    );
  }

  const initials = (me.name ?? me.email).slice(0, 2).toUpperCase();

  return (
    <div className="page space-y-8">
      <header className="flex items-center gap-4">
        <div className="grid size-14 shrink-0 place-items-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))] text-lg font-medium">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">Account</p>
          <h1 className="text-[2rem] md:text-[2.5rem] font-normal leading-tight mt-1 text-ink truncate">
            {me.name ?? 'Your profile'}
          </h1>
          <p className="mt-1 mono-meta text-muted-foreground truncate">
            {me.email} · {me.role}
          </p>
        </div>
      </header>

      {sub && <PlanCard status={sub.status} renewBy={sub.renewBy} />}

      {/* Details */}
      <section className="card-paper p-5 sm:p-6 space-y-4">
        <h2 className="text-lg font-medium text-ink leading-tight">Details</h2>
        <form className="space-y-4" onSubmit={saveProfile}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="name" className="field-label">
                Name
              </label>
              <input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="input"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="nameDv" className="field-label">
                Name (Dhivehi)
              </label>
              <input
                id="nameDv"
                dir="rtl"
                value={nameDv}
                onChange={(e) => setNameDv(e.target.value)}
                placeholder="ނަން"
                className="input font-dhivehi text-base text-right"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label htmlFor="dv" className="field-label">
                Designation (Dhivehi)
              </label>
              <input
                id="dv"
                dir="rtl"
                value={designationDv}
                onChange={(e) => setDesignationDv(e.target.value)}
                placeholder="މަޤާމު"
                className="input font-dhivehi text-base text-right"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="en" className="field-label">
                Designation (English)
              </label>
              <input
                id="en"
                value={designationEn}
                onChange={(e) => setDesignationEn(e.target.value)}
                placeholder="e.g. Council Secretary"
                className="input"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="section" className="field-label">
              Section
            </label>
            <input
              id="section"
              value={section}
              onChange={(e) => setSection(e.target.value)}
              placeholder="e.g. Finance Section"
              className="input"
            />
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={savePending} className="btn-primary">
              <Icon name="save" size={18} /> {savePending ? 'Saving…' : 'Save details'}
            </button>
            {saveMsg && <span className="text-sm text-muted-foreground">{saveMsg}</span>}
          </div>
        </form>
      </section>

      {/* Signature */}
      <section className="card-paper p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-ink leading-tight">Signature image</h2>
            <p className="text-xs text-muted-foreground mt-1">Square PNG, 64–2048px, up to 2MB.</p>
          </div>
          {me.hasSignature && <span className="pill pill-success shrink-0">On file</span>}
        </div>
        <div className="flex items-center gap-4">
          {me.hasSignature && sigBlobUrl ? (
            <img
              src={sigBlobUrl}
              alt="Your saved signature"
              className="size-24 shrink-0 rounded-xl bg-[hsl(var(--surface-container-high))] object-contain"
            />
          ) : me.hasSignature ? (
            <div className="size-24 shrink-0 grid place-items-center rounded-xl bg-[hsl(var(--surface-container-high))] text-xs text-muted-foreground">
              Loading…
            </div>
          ) : (
            <div className="size-24 shrink-0 grid place-items-center rounded-xl bg-[hsl(var(--surface-container-high))] text-sm text-muted-foreground">
              None
            </div>
          )}
          <div className="flex-1 min-w-0 space-y-2">
            <label htmlFor="sig-upload" className="sr-only">
              Upload signature image
            </label>
            <input
              id="sig-upload"
              ref={fileRef}
              type="file"
              accept="image/png"
              aria-label="Upload signature image"
              className="block w-full text-xs text-muted-foreground file:mr-3 file:rounded-full file:border-0 file:bg-[hsl(var(--secondary-container))] file:px-4 file:py-1.5 file:text-xs file:font-medium file:text-[hsl(var(--on-secondary-container))]"
            />
            <div className="flex flex-wrap gap-2">
              <button onClick={() => void uploadSig()} disabled={uploading} className="btn-primary">
                <Icon name="upload" size={18} />
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              {me.hasSignature && (
                <button onClick={() => void removeSig()} className="btn-danger">
                  <Icon name="delete" size={18} /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <p className="text-xs text-muted-foreground">Your signature is used across Wapplify apps.</p>
      </section>

      {/* Password */}
      <section className="card-paper p-5 sm:p-6 space-y-4">
        <h2 className="text-lg font-medium text-ink leading-tight">Change password</h2>
        <form className="space-y-3 max-w-sm" onSubmit={changePassword}>
          <input
            type="password"
            placeholder="Current password"
            aria-label="Current password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            className="input"
          />
          <input
            type="password"
            placeholder="New password (≥ 8 chars)"
            aria-label="New password"
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            className="input"
          />
          <div className="flex items-center gap-3">
            <button type="submit" disabled={pwPending} className="btn-primary">
              <Icon name="key" size={18} /> {pwPending ? 'Updating…' : 'Update password'}
            </button>
            {pwMsg && <span className="text-sm text-muted-foreground">{pwMsg}</span>}
          </div>
          {pwErr && <p className="text-sm text-destructive">{pwErr}</p>}
        </form>
      </section>
    </div>
  );
}
